import { themeVar } from '@/styles/theme';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ExportDataIcon } from '@/assets/icons/exportDataIcon';
import { FilterIcon } from '@/assets/icons/filter';
import { FilterSettingIcon } from '@/assets/icons/filterSetting';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { RefreshIcon } from '@/assets/icons/info-icons/refresh';
import { SelectIcon } from '@/assets/icons/selectIcon';
import FilterBar, { IFilterBarData } from '@/components/basicComponents/filterBar';
import Input from '@/components/basicComponents/input';
import Search from '@/components/basicComponents/search';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { IToggle, Toggle } from '@/components/basicComponents/toggle';
import { AnimatePresence, motion } from 'framer-motion';
import SelectWithCheckBox, { TData } from '../../basicComponents/selectWithCheckBox';
import ChosenFilters from './chosenFilters';
import MoreFilters from './moreFilters';
import { AdditionalFilters, IFilterDownloadButton, IQuickFilter } from './types';

interface FilterPageProps {
  search?: {
    value: string;
    onChange: any;
    placeholder: string;
  };
  primaryFilter?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
    withSearch?: boolean;
  };
  withCheckBox?: boolean;
  additionalFilters?: AdditionalFilters;
  setAllocationStatusFilter?: any;
  allocationStatusFilter?: TData[];
  currentTab?: { value: string };
  className?: string;
  isResult?: boolean;
  resultsValue?: number;
  refetch?: any;
  isDealRegistryFilter?: boolean;
  handleTableColumns?: () => void;
  viewBySelect?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
  };
  filterBar?: IFilterBarData[];
  onChangeFilterBar?: (filterBarItems: IFilterBarData[]) => void;
  useNewFilters?: boolean;
  advisoryFirmQuickFilter?: IQuickFilter;
  viewByQuickFilter?: IQuickFilter;
  quickFilter?: IQuickFilter;
  toggle?: IToggle;
  downloadButton?: IFilterDownloadButton;
}

const DesktopFilterPage = ({
  search,
  primaryFilter,
  additionalFilters,
  withCheckBox,
  setAllocationStatusFilter,
  allocationStatusFilter,
  currentTab,
  isResult,
  resultsValue,
  refetch,
  handleTableColumns,
  viewBySelect,
  isDealRegistryFilter = false,
  filterBar,
  onChangeFilterBar,
  useNewFilters = false,
  advisoryFirmQuickFilter,
  viewByQuickFilter,
  quickFilter,
  toggle,
  downloadButton
}: FilterPageProps) => {
  const [moreFilter, setMoreFilter] = useState(false);
  const [chosenFilters, setChosenFilters] = useState(!!additionalFilters?.showChosenFilters);
  const [checkBoxData, setCheckBoxData] = useState<TData[]>(allocationStatusFilter ?? []);

  useEffect(() => {
    if (additionalFilters?.filters[0].typeFilter === 'Select A Filter Option' && currentTab) {
      setChosenFilters(false);
    }
  }, [currentTab?.value, additionalFilters]);

  useEffect(() => {
    if (additionalFilters?.filters[0].typeFilter !== 'Select A Filter Option') {
      setChosenFilters(true);
    }
  }, []);

  useEffect(() => {
    if (isDealRegistryFilter) {
      setChosenFilters(!!additionalFilters?.showChosenFilters);
    }
  }, [additionalFilters?.showChosenFilters]);

  const checkBoxSelectLabel = useMemo(() => {
    const choosenNames = checkBoxData.filter((el) => el.value);
    let label = '';
    if (choosenNames.length >= 1 && choosenNames.length <= 2) {
      choosenNames.map((el, index) => {
        if (index === 0) {
          label = el.name;
        } else {
          label = label + ', ' + el.name;
        }
      });
      return label;
    } else {
      return choosenNames.length === 0 ? 'Filter by' : choosenNames.length + ' ' + 'Selected';
    }
  }, [checkBoxData]);

  const handleCheckBoxData = (id: number) => {
    const newData = [...checkBoxData];
    newData[id].value = !newData[id].value;
    setCheckBoxData(newData);
  };

  if (useNewFilters) {
    return (
      <FiltersWrapper>
        {filterBar && (
          <FilterBarWrap>
            <FilterBar filterBar={filterBar} onChangeFilterBar={onChangeFilterBar} />
          </FilterBarWrap>
        )}
        <TableToolbar>
          <Filters>
            {isResult ? (
              <RefreshWrapper>
                <Results>{additionalFilters?.amountResults || resultsValue || 0} Results</Results>
                <RefreshButton
                  onClick={() => {
                    if (additionalFilters?.onRefetch) {
                      additionalFilters.onRefetch();
                    } else if (refetch) {
                      refetch();
                    } else null;
                  }}
                >
                  <RefreshIcon width={15} height={15} fill={themeVar('font.action')} />
                </RefreshButton>
              </RefreshWrapper>
            ) : null}
            {viewByQuickFilter && (
              <QuickFilterWrapper>
                <QuickFilterTitle>{viewByQuickFilter.title}</QuickFilterTitle>
                <QuickFilterSelect
                  value={viewByQuickFilter.value}
                  setValue={viewByQuickFilter.onChange}
                  defaultSelectLabel={viewByQuickFilter.defaultValue}
                  options={viewByQuickFilter.options}
                  size="sm"
                  customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                />
              </QuickFilterWrapper>
            )}
            {advisoryFirmQuickFilter && (
              <QuickFilterWrapper>
                <QuickFilterTitle>{advisoryFirmQuickFilter.title}</QuickFilterTitle>
                <QuickFilterSelect
                  value={advisoryFirmQuickFilter.value}
                  setValue={advisoryFirmQuickFilter.onChange}
                  defaultSelectLabel={advisoryFirmQuickFilter.defaultValue}
                  options={advisoryFirmQuickFilter.options}
                  centerList={advisoryFirmQuickFilter?.centerList}
                  size="sm"
                  customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                />
              </QuickFilterWrapper>
            )}
            {search && <Search value={search.value} onChange={search.onChange} placeholder={search.placeholder} />}
          </Filters>

          <RightAlignedFilters>
            {downloadButton && (
              <DownloadButtonWrapper onClick={downloadButton.onClick}>
                <div>
                  <ExportDataIcon fill={themeVar('font.action')} />
                </div>
                <DownloadButtonTitle>{downloadButton?.title || 'Export Data'}</DownloadButtonTitle>
              </DownloadButtonWrapper>
            )}
            {quickFilter && (
              <QuickFilterWrapper>
                <QuickFilterTitle>{quickFilter.title}</QuickFilterTitle>
                <QuickFilterSelect
                  value={quickFilter.value}
                  setValue={quickFilter.onChange}
                  defaultSelectLabel={quickFilter.defaultValue}
                  options={quickFilter.options}
                  size="sm"
                  customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                />
              </QuickFilterWrapper>
            )}
            {toggle && (
              <div>
                <Toggle title={toggle.title} isActive={toggle.isActive} onToggle={toggle.onToggle} />
              </div>
            )}
            {additionalFilters && (
              <FilterWrap onClick={() => setMoreFilter(!moreFilter)} moreFilter={moreFilter}>
                {moreFilter ? (
                  <CloseInfoIcon fill={themeVar('context.light')} width={11} height={11} />
                ) : (
                  <FilterIcon fill={themeVar('font.action')} />
                )}
              </FilterWrap>
            )}
            {handleTableColumns && (
              <FilterSettingWrap onClick={handleTableColumns}>
                <FilterSettingIcon fill={themeVar('font.action')} />
              </FilterSettingWrap>
            )}
          </RightAlignedFilters>
        </TableToolbar>
        <AnimatePresence>
          {additionalFilters && moreFilter && (
            <motion.div
              key="more-filters"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <MoreFilters onClose={() => setMoreFilter(false)} setChosenFilters={setChosenFilters} {...additionalFilters} />
            </motion.div>
          )}
          {additionalFilters && chosenFilters && (
            <motion.div key="chosen-filters" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ChosenFilters setChosenFilters={setChosenFilters} {...additionalFilters} />
            </motion.div>
          )}
        </AnimatePresence>
      </FiltersWrapper>
    );
  }

  return (
    <>
      <DefaultSearchWrap>
        {isResult ? (
          <TopWrapper>
            <Results>{additionalFilters?.amountResults || resultsValue || 0} Results</Results>
            <RefreshButton
              onClick={() => {
                if (additionalFilters?.onRefetch) {
                  additionalFilters.onRefetch();
                } else if (refetch) {
                  refetch();
                } else null;
              }}
            >
              <RefreshIcon width={15} height={15} fill={themeVar('action.primary')} />
            </RefreshButton>
            {viewBySelect && (
              <QuickFilterWrapper padding="5px 10px 5px 30px">
                <QuickFilterTitle>View By</QuickFilterTitle>
                <QuickFilterSelect
                  value={viewBySelect.value}
                  setValue={viewBySelect.onChange}
                  options={viewBySelect.selects.map((item) => createSimpleSelectOption(item))}
                  centerList={true}
                  size="sm"
                  customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                />
              </QuickFilterWrapper>
            )}
          </TopWrapper>
        ) : null}

        <MainSearchWrap>
          {downloadButton && (
            <DownloadButtonWrapper onClick={downloadButton.onClick}>
              <ExportDataIcon fill={themeVar('font.action')} />
              <DownloadButtonTitle>Export Data</DownloadButtonTitle>
            </DownloadButtonWrapper>
          )}
          <div>
            <Input
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder}
              width="250px"
              size="sm"
              withSearchIcon
            />
          </div>
          {primaryFilter && (
            <Select
              value={primaryFilter.value}
              setValue={primaryFilter.onChange}
              options={primaryFilter.selects.map((item) => createSimpleSelectOption(item))}
              withSearch={!!primaryFilter?.withSearch}
              width="204px"
              size="sm"
            />
          )}
          {currentTab?.value === 'pending' && withCheckBox && (
            <SelectWithCheckBox
              data={checkBoxData}
              setSelected={handleCheckBoxData}
              label={checkBoxSelectLabel}
              setAllocationStatusFilter={setAllocationStatusFilter}
              setCheckBoxData={setCheckBoxData}
            />
          )}
          {additionalFilters && (
            <FilterWrap onClick={() => setMoreFilter(!moreFilter)} moreFilter={moreFilter}>
              <FilterIcon fill={moreFilter ? themeVar('context.light') : themeVar('font.action')} />
            </FilterWrap>
          )}
          {handleTableColumns && (
            <FilterSettingWrap onClick={handleTableColumns}>
              <FilterSettingIcon fill={themeVar('font.action')} />
            </FilterSettingWrap>
          )}
        </MainSearchWrap>
      </DefaultSearchWrap>

      {additionalFilters && moreFilter && (
        <MoreFilters onClose={() => setMoreFilter(false)} setChosenFilters={setChosenFilters} {...additionalFilters} />
      )}
      {additionalFilters && chosenFilters && <ChosenFilters setChosenFilters={setChosenFilters} {...additionalFilters} />}
      {filterBar && (
        <FilterBarWrap>
          <FilterBar filterBar={filterBar} onChangeFilterBar={onChangeFilterBar} />
        </FilterBarWrap>
      )}
    </>
  );
};

export default DesktopFilterPage;

const DownloadButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 6px;
  padding: 5px 20px;
`;

const DownloadButtonTitle = styled.p`
  font-family: Blinker, serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 18.2px;
  white-space: nowrap;
  color: ${themeVar('font.action')};
`;

const FilterWrap = styled.div<{ moreFilter: boolean }>`
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ moreFilter }) => (moreFilter ? themeVar('action.default') : themeVar('layer.base'))};
  border: ${({ moreFilter }) => `1px solid ${moreFilter ? themeVar('action.default') : themeVar('font.action')}`};
  border-radius: 3px;
`;

export const FilterSettingWrap = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${themeVar('layer.base')};
  border: 1px solid ${themeVar('font.action')};
  border-radius: 3px;
  padding: 8px;
`;

const DefaultSearchWrap = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 22px;
`;

const MainSearchWrap = styled.div`
  display: flex;
  border-radius: 4px;
  background: ${themeVar('layer.1')};
  padding: 8px 10px;
  gap: 20px;
`;

export const FilterBarWrap = styled.div`
  margin-top: 10px;
`;

export const TopWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

export const Results = styled.h1`
  color: ${themeVar('font.base')};
  font-family: Blinker;
  font-size: 13px;
  font-weight: 600;
  line-height: 13px;
`;

export const RefreshButton = styled.div`
  padding-left: 5px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const TableToolbar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 0 10px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Filters = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RightAlignedFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: auto;
`;

export const RefreshWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const QuickFilterWrapper = styled.div<{ padding?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  padding: ${({ padding }) => padding || '0'};
`;

export const QuickFilterTitle = styled.p`
  font-family: Blinker;
  font-size: 13px;
  font-weight: 400;
  line-height: 13px;
  white-space: nowrap;
  color: ${themeVar('font.strong')};
`;

export const QuickFilterSelect = styled(Select)`
  border: 0;
  padding: 0;
  background: transparent;
  justify-content: flex-start;
  max-width: 150px;
  width: max-content;

  svg {
    margin: 0 6px;
  }

  #titleSelect {
    color: ${themeVar('font.action')};
  }

  #dropdownSelect {
    top: 26px;
    width: max-content;
  }
`;
